import { useState, useEffect, useCallback } from 'react';
import {
  NavLink,
  useNavigate,
  generatePath,
  useParams,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  ResponseError,
  responseErrorsResolver,
  formatDate,
  ApplicationSelector,
} from '@ui/src';
import {
  useAddCorporateCOFMutation,
  useLazyGetCorporateCOFQuery,
} from '../store/services/corporate';
import SelectCompanyOfficialForm from '../../../components/corporate/overview/select-company-official-form';
import {
  CofProps,
  NatureOfControlEnum,
  NewCofProps,
} from '../../customers/types';
import useGetAutocompleteList from '../../new-application/helpers/use-autocomplete-list';
import { AccessRight, SignatureType } from '../types';

import ROUTES from '../../../routes';

import cn from '../corporate.module.css';

const SelectCompanyOfficial = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [formValues, setFormValues] = useState<NewCofProps>();
  const [percentageError, setPercentageError] = useState('');
  const [cofError, setCofError] = useState('');

  const {
    setError,
    formState: { errors },
  } = useForm();

  const { customerId } = useParams();

  const navigate = useNavigate();

  const [addNewCof, { isLoading, isSuccess }] = useAddCorporateCOFMutation();

  const [
    getCorporateCof,
    { isLoading: cofIsLoading, isSuccess: cofLoadingIsSuccess },
  ] = useLazyGetCorporateCOFQuery();

  const {
    autocompleteList,
    onSearch,
    isLoading: autocompleteIsLoading,
    searchQuery,
    setSearchQuery,
    getAutocompleteListAction,
  } = useGetAutocompleteList();

  const addNewCofAction = useCallback(async () => {
    setCofError('');

    const body: {
      userId: string;
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
      organizationId: string;
      position: string;
      companyOfficialType: string;
      signatureType: SignatureType;
      dob?: string;
      percentage?: string;
      natureOfControl?: NatureOfControlEnum;
      accessRight: AccessRight;
    } = {
      userId: formValues?.userId as string,
      email: formValues?.email as string,
      firstName: formValues?.firstName as string,
      lastName: formValues?.lastName as string,
      phone: formValues?.phone as string,
      organizationId: customerId as string,
      position: formValues?.position as string,
      companyOfficialType:
        formValues?.companyOfficialType || 'COMPANY_OFFICIAL',
      signatureType: formValues?.signatureType as SignatureType,
      accessRight:
        (formValues?.accessRight as AccessRight) || AccessRight.VIEWER,
    };

    if (formValues?.dob) {
      const dob = new Date(formValues.dob);

      body.dob = `${dob.getFullYear()}-${(dob.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${dob.getDate().toString().padStart(2, '0')}`;
    }

    if (formValues?.havingControl) {
      body.percentage = formValues.percentage;
      body.natureOfControl = formValues.natureOfControl;
    }

    const response = (await addNewCof(body)) as {
      data?: { data: CofProps };
      error?: ResponseError | { data: { message: string } };
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError,
        setGlobalError: setCofError,
      });
    }
  }, [formValues, customerId]);

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(-1);
    }, 100);
  };

  useEffect(() => {
    if (isSuccess && customerId) {
      getCorporateCof(customerId);
    }
  }, [isSuccess, customerId]);

  useEffect(() => {
    if (cofLoadingIsSuccess) {
      handleClose();
    }
  }, [cofLoadingIsSuccess]);

  useEffect(() => {
    if (!panelIsOpen) {
      setPanelIsOpen(true);
    }
  }, [panelIsOpen]);

  useEffect(() => {
    if (
      formValues &&
      formValues.percentage &&
      !/^\d*\.?\d*$/.test(formValues.percentage)
    ) {
      setPercentageError('Percentage of control field must be numeric');
    } else {
      setPercentageError('');
    }
  }, [formValues]);

  let link = '';
  let linkText = '';

  if (selectedCustomerId) {
    const currentApplication = autocompleteList.find(
      (item) => item.id === selectedCustomerId,
    );

    if (currentApplication) {
      if (currentApplication.organizationId) {
        link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_PAGE, {
          customerId: currentApplication.organizationId,
        });

        linkText = 'Go to customer';
      } else {
        linkText = 'Go to application';

        if (currentApplication.type === 'COMPANY_OFFICIAL') {
          link = generatePath(
            ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL,
            {
              applicationId: selectedCustomerId,
            },
          );
        } else if (currentApplication.type === 'INDIVIDUAL') {
          link = generatePath(
            ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_INFO,
            {
              applicationId: selectedCustomerId,
            },
          );
        }
      }
    }
  }

  return (
    <SidePanel
      type={SidePanelType.Customers}
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={handleClose}
      title="Add Company official application"
      sidepanelExpandedKey="corporate-create-cof"
      footer={
        <>
          <ButtonPrimary
            text={isLoading || cofIsLoading ? 'Loading...' : 'Add'}
            className={cn.addNewCofButton}
            onClick={addNewCofAction}
            disabled={
              !selectedCustomerId ||
              !formValues?.position ||
              !formValues.signatureType ||
              isLoading ||
              cofIsLoading
            }
            bold
          />
          <ButtonPseudo text="Close" onClick={handleClose} bold />
        </>
      }
    >
      <>
        <div className={cn.selectCompanyOfficialTitle}>
          Select a customer to be appointed as an company official
        </div>
        <div className={cn.selectCompanyOfficialWrapper}>
          <div className={cn.companyOfficialAutocomplete}>
            <ApplicationSelector
              placeholder="Select customer..."
              list={autocompleteList.map((item) => ({
                ...item,
                dob: formatDate(item?.dob || '') || '-',
              }))}
              onSearch={onSearch}
              isLoading={autocompleteIsLoading}
              error={cofError}
              onApplicationClick={(id: string) => {
                const currentCustomer = autocompleteList?.find(
                  (item) => item.id === id,
                );
                setSelectedCustomerId(id);

                if (currentCustomer) {
                  setFormValues({
                    firstName: currentCustomer?.firstName || '',
                    lastName: currentCustomer?.lastName || '',
                    dob: currentCustomer?.dob || '',
                    phone: currentCustomer?.phone,
                    email: currentCustomer?.email as string,
                  });
                }

                if (searchQuery) {
                  setSearchQuery('');
                  getAutocompleteListAction('');
                }
              }}
              onButtonClick={() => {}}
              searchQuery={searchQuery}
              fullWidth
              withoutButton
              withClearButton
            />
          </div>
          {selectedCustomerId && (
            <NavLink
              to={link}
              className={`${cn.customerLink} ${
                !formValues ? cn.customerLinkDisabled : ''
              }`}
              target="_blank"
            >
              {linkText}
            </NavLink>
          )}
        </div>
        <div className={cn.companyOfficialFormContent}>
          <SelectCompanyOfficialForm
            customer={formValues}
            havingControl={formValues?.havingControl}
            percentageError={percentageError}
            setFormValues={setFormValues}
            customerId={selectedCustomerId}
            errors={errors}
          />
        </div>
      </>
    </SidePanel>
  );
};

export default SelectCompanyOfficial;
